import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { API } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function GlomtLosenord() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error("Ange din e-postadress");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            setDone(true);
        } catch {
            // Backend always returns success to prevent enumeration —
            // this catch is defensive only
            setDone(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="page-glomt-losenord"
            className="relative min-h-[80vh] grid place-items-center py-20"
        >
            <div className="relative max-w-lg w-full mx-4 bg-[#111113] border border-white/10 rounded-sm p-10 lg:p-14">
                {done ? (
                    <div data-testid="forgot-success">
                        <div className="w-14 h-14 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                            <Check size={22} />
                        </div>
                        <p className="mt-6 overline">Mail skickat</p>
                        <h1 className="mt-3 font-serif font-light text-3xl sm:text-4xl tracking-tight leading-[1.15]">
                            Kolla din{" "}
                            <span className="italic text-[#C4A977]">inkorg</span>
                        </h1>
                        <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">
                            Om <span className="text-[#F4F4F5]">{email}</span> finns
                            registrerad har vi skickat en återställningslänk dit.
                            Länken är giltig i 60 minuter.
                        </p>
                        <p className="mt-3 text-xs text-[#A1A1AA]/70 leading-relaxed">
                            Hittar du inte mailet? Kolla skräpposten. Det kan ta
                            upp till en minut innan det dyker upp.
                        </p>
                        <Link
                            to="/logga-in"
                            className="mt-8 inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                        >
                            Tillbaka till inloggning
                            <ArrowRight
                                size={14}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="w-14 h-14 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                            <Mail size={22} />
                        </div>
                        <p className="mt-6 overline">Glömt lösenord?</p>
                        <h1 className="mt-3 font-serif font-light text-3xl sm:text-4xl tracking-tight leading-[1.15]">
                            Vi skickar en{" "}
                            <span className="italic text-[#C4A977]">
                                återställningslänk
                            </span>
                        </h1>
                        <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">
                            Ange den e-postadress du registrerat ditt konto med
                            så skickar vi en länk där du kan välja ett nytt
                            lösenord.
                        </p>

                        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                    E-post
                                </Label>
                                <Input
                                    data-testid="forgot-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] h-11 mt-2"
                                    autoComplete="email"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="forgot-submit"
                                className="w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 text-sm tracking-wide font-medium"
                            >
                                {submitting ? "Skickar..." : "Skicka återställningslänk"}
                            </Button>
                        </form>

                        <Link
                            to="/logga-in"
                            className="mt-6 inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F4F4F5] text-sm"
                        >
                            ← Tillbaka till inloggning
                        </Link>

                        <div className="mt-8 flex items-start gap-3 text-xs text-[#A1A1AA]/80">
                            <ShieldCheck
                                size={14}
                                className="text-[#C4A977] mt-0.5 shrink-0"
                            />
                            <p>
                                Av säkerhetsskäl visar vi alltid samma meddelande
                                — oavsett om kontot finns eller ej.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
